/**
 * CalendarSettingsAdmin.tsx — Admin > Appointments > Calendar Settings: the
 * scheduler's display, status colours, appointment stage rules and info pop-up.
 *
 * Changes are tracked against what was loaded, as the Admin forms do: each
 * changed setting shows the "modified" pill with its own revert (the
 * Additional Rules list as one), and the header shows "Unsaved changes" with
 * Revert for all beside Save Settings.
 *
 * Locked (plan 2026-10-10-001 U7: R8, R18): the settings sit in an
 * AdminLockedFieldset and show read-only — a colour shows as a swatch with its
 * hex — with no Save Settings, Add Rule or Remove rule.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { DialogField, ModifiedPill, SwitchField } from '@/components/admin/DialogChrome';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Checkbox } from '@/components/ui/Checkbox';
import { useFieldLocked } from '@/components/ui/FieldLock';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { toast } from '@/store/toastStore';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import { getErrorMessage } from '@/services/apiClient';
import * as adminSvc from '@/services/admin.service';
import type {
  CalendarApptChangeRules,
  CalendarInfoFlags,
  CalendarSettings,
  CalendarSettingsWithOptions,
  CalendarStageRule,
  CalendarStatusColorPalette,
} from '@/services/admin.service';

// ─── Section definitions ──────────────────────────────────────────────────────

const FONT_SIZE_OPTIONS = [10, 11, 12, 13, 14, 15, 16].map((n) => ({
  value: `${n}px`,
  label: `${n}px`,
}));

/** Legacy "Scheduler Display" block (admin_calendar_details.php:462-560): legacy's Enable / Disable selects, shown as switches. */
const APPEARANCE_TOGGLES: { key: 'draggableAppts' | 'statusColors' | 'schedulerGradients' | 'schedulerResources'; label: string }[] = [
  { key: 'draggableAppts', label: 'Draggable & Resizable Appointments' },
  { key: 'statusColors', label: 'Border Color Indicates Status' },
  { key: 'schedulerGradients', label: 'Color Gradients' },
  { key: 'schedulerResources', label: 'Display Resources on Appt. Cell' },
];

/** Legacy "Status Colors" block (admin_calendar_details.php:562-593). */
const PALETTE_FIELDS: { key: keyof CalendarStatusColorPalette; label: string }[] = [
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'checkedIn', label: 'Checked In' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'checkedBack', label: 'Checked Back' },
  { key: 'waitList', label: 'Wait List' },
  { key: 'checkedOut', label: 'Checked Out' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'noShow', label: 'No Show' },
  { key: 'contactAttempted', label: 'Contact Attempted' },
];

/**
 * The 6 fixed stage rules (admin_calendar_details.php:598-704). `stageTitles`
 * maps each non-zero option value to the stage id whose account-specific title
 * fills the "change plan stage to X" label.
 */
const FIXED_RULES: {
  key: keyof CalendarApptChangeRules;
  label: string;
  stageTitles: Record<string, string>;
}[] = [
  { key: 'apptChangeRule1', label: 'When scheduling a Consultation appointment', stageTitles: { '1': '2' } },
  { key: 'apptChangeRule3', label: 'When scheduling a Surgery appointment', stageTitles: { '1': '4' } },
  { key: 'apptChangeRule5', label: 'When scheduling a Non-Surgical appointment', stageTitles: { '1': '4', '2': '21' } },
  { key: 'apptChangeRule2', label: "When changing a Consultation appointment to 'Checked In' or 'Checked Out'", stageTitles: { '1': '3' } },
  { key: 'apptChangeRule4', label: "When changing a procedure appointment to 'Checked Out'", stageTitles: { '1': '6' } },
  { key: 'apptChangeRule6', label: "When changing a procedure appointment to 'Cancelled'", stageTitles: { '1': '5' } },
];

const RULE_ACTION_OPTIONS = [
  { value: 'scheduled', label: 'scheduled' },
  { value: 'checked in', label: 'checked in' },
  { value: 'checked out', label: 'checked out' },
  { value: 'cancelled', label: 'cancelled' },
];

/**
 * Legacy "Info Pop-up" block (admin_calendar_details.php:883-952), in page order.
 * `roleType` marks the two labels legacy renders from the account's configured
 * role titles (`Plan <roleTitleDoctor>:` / `Plan <roleTitleCoordinator>:`). The
 * value is an `aa_accounts_roles.roleType`, which is singular and capitalized.
 */
const INFO_FLAGS: { key: keyof CalendarInfoFlags; label: string; roleType?: string }[] = [
  { key: 'infoTags', label: 'Tags' },
  { key: 'infoPhoto', label: 'Photo' },
  { key: 'infoChartId', label: 'Chart ID' },
  { key: 'infoPhone', label: 'Phone' },
  { key: 'infoStatus', label: 'Appointment Status' },
  { key: 'infoDob', label: 'Date of Birth' },
  { key: 'infoCheckedBackWait', label: 'Checked-back Wait' },
  { key: 'infoDoctor', label: 'Doctor', roleType: 'Doctor' },
  { key: 'infoApptName', label: 'Appointment Name' },
  { key: 'infoPlanStage', label: 'Plan Stage' },
  { key: 'infoPatientNotes', label: 'Patient Notes' },
  { key: 'infoCheckedInWait', label: 'Checked-in Wait' },
  { key: 'infoApptNotes', label: 'Appointment Notes' },
  { key: 'infoApptType', label: 'Appointment Type' },
  { key: 'infoCoordinator', label: 'Coordinator', roleType: 'Coordinator' },
  { key: 'infoFacility', label: 'Appointment Facility' },
  { key: 'infoMemberships', label: 'Memberships & Packages' },
  { key: 'infoResources', label: 'Appointment Resources' },
];

// ─── Local form state ─────────────────────────────────────────────────────────

/**
 * Rules carry a stable client-side key so React can track rows that have no
 * database id yet. Legacy tracked this with a hidden comma-separated field of
 * `NEW1`-style tokens (admin_calendar_details.php:404-425).
 */
interface RuleRow extends Omit<CalendarStageRule, 'standardType' | 'appointmentTypeGroupID'> {
  rowKey: string;
}

interface FormState {
  infoFlags: Record<string, number>;
  appearance: {
    draggableAppts: number;
    statusColors: number;
    schedulerGradients: number;
    schedulerResources: number;
    schedulerFontSize: string | null;
  };
  palette: CalendarStatusColorPalette;
  apptChangeRules: CalendarApptChangeRules;
  stageRules: RuleRow[];
}

/** The form's per-setting sections; Additional Rules is tracked as one list. */
type TrackedSection = 'appearance' | 'palette' | 'apptChangeRules' | 'infoFlags';

let ruleKeySeq = 0;
function nextRuleKey(): string {
  ruleKeySeq += 1;
  return `new-${ruleKeySeq}`;
}

/**
 * The legacy columns are strings (`'1'` / `'0'`), and the driver may hand them
 * back either way — compare numerically rather than with `=== 1`.
 */
function flagOn(value: unknown): number {
  return Number(value) === 1 ? 1 : 0;
}

function toFormState(data: CalendarSettings): FormState {
  const infoFlags: Record<string, number> = {};
  for (const f of INFO_FLAGS) {
    infoFlags[f.key] = flagOn((data as unknown as Record<string, unknown>)[f.key]);
  }
  infoFlags.displayDailyAlert = flagOn(data.displayDailyAlert);

  return {
    infoFlags,
    appearance: {
      draggableAppts: flagOn(data.appearance?.draggableAppts),
      statusColors: flagOn(data.appearance?.statusColors),
      schedulerGradients: flagOn(data.appearance?.schedulerGradients),
      schedulerResources: flagOn(data.appearance?.schedulerResources),
      schedulerFontSize: data.appearance?.schedulerFontSize ?? null,
    },
    palette: { ...data.statusColorPalette },
    apptChangeRules: { ...data.apptChangeRules },
    stageRules: (data.stageRules ?? []).map((r) => ({
      rowKey: `db-${r.appointmentStageRuleID}`,
      appointmentStageRuleID: r.appointmentStageRuleID,
      type: r.type,
      apptAction: r.apptAction,
      stageID: r.stageID,
    })),
  };
}

// ─── Main component ───────────────────────────────────────────────────────────

export function CalendarSettingsAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [settings, setSettings] = useState<CalendarSettingsWithOptions | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [savedForm, setSavedForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  // Legacy renders the Doctor / Coordinator labels from the account's own role
  // titles, so an account calling them "Physician" / "ECC" sees that here too.
  // `aa_accounts_roles.roleType` holds the singular capitalized form — match the
  // convention used by PlansTab, NewLeadPage, PatientsPage and the contact-list
  // pages, not the plural resource-type keys `useRoleTitles` defaults to.
  const { roles } = useRoleTitles();
  const roleLabel = useCallback(
    (roleType: string | undefined, fallback: string) => {
      if (!roleType) return fallback;
      return roles.find((r) => r.roleType === roleType)?.roleTitle?.trim() || fallback;
    },
    [roles],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await adminSvc.getCalendarSettingsWithOptions();
      setSettings(data);
      const next = toFormState(data);
      setForm(next);
      setSavedForm(next);
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to load calendar settings.');
      setLoadError(message);
      toast.error('Failed to load calendar settings', getErrorMessage(err, ''));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = useMemo(
    () => !!form && !!savedForm && JSON.stringify(form) !== JSON.stringify(savedForm),
    [form, savedForm],
  );

  /** One setting's "modified" pill: shown while it differs from what was loaded; its revert puts that value back. */
  function fieldPill(section: TrackedSection, key: string, label: string): ReactNode {
    if (!form || !savedForm) return null;
    const was = (savedForm[section] as Record<string, unknown>)[key];
    if ((form[section] as Record<string, unknown>)[key] === was) return null;
    return (
      <ModifiedPill
        label={label}
        onRevert={() => patch((f) => ({ ...f, [section]: { ...(f[section] as Record<string, unknown>), [key]: was } }))}
      />
    );
  }

  /** The Additional Rules list as one: any rule added, removed or changed. */
  const rulesPill =
    form && savedForm && JSON.stringify(form.stageRules) !== JSON.stringify(savedForm.stageRules) ? (
      <ModifiedPill label="Additional Rules" onRevert={() => patch((f) => ({ ...f, stageRules: savedForm.stageRules }))} />
    ) : null;

  /** Built-in classes first, then the account's own type groups — legacy order. */
  const typeOptions = useMemo(() => {
    if (!settings) return [];
    return [
      ...settings.options.standardTypes.map((t) => ({ value: t.value, label: t.label })),
      ...settings.options.appointmentTypeGroups.map((g) => ({
        value: String(g.appointmentTypeGroupID),
        label: g.name,
      })),
    ];
  }, [settings]);

  const stageOptions = useMemo(
    () => (settings?.options.stages ?? []).map((s) => ({ value: s.value, label: s.label })),
    [settings],
  );

  function patch(updater: (draft: FormState) => FormState) {
    setForm((f) => (f ? updater(f) : f));
  }

  async function handleSave() {
    if (!form || locked) return;
    setSaving(true);
    try {
      const updated = await adminSvc.updateCalendarSettings({
        ...(form.infoFlags as Record<string, number>),
        appearance: form.appearance,
        statusColorPalette: form.palette,
        apptChangeRules: form.apptChangeRules,
        stageRules: form.stageRules.map((r) => ({
          // Omit the id for rows that have never been saved so the API inserts.
          ...(r.appointmentStageRuleID > 0
            ? { appointmentStageRuleID: r.appointmentStageRuleID }
            : {}),
          type: r.type,
          apptAction: r.apptAction,
          stageID: r.stageID,
        })),
      });
      setSettings(updated);
      const next = toFormState(updated);
      setForm(next);
      setSavedForm(next);
      // The scheduler caches both of these for 10 minutes, and this save writes
      // the columns behind both (info* flags + the aa_accounts appearance and
      // palette columns). Without invalidation an already-open SchedulePage tab
      // keeps rendering pre-save settings.
      void queryClient.invalidateQueries({ queryKey: ['admin-calendar-settings'] });
      void queryClient.invalidateQueries({ queryKey: ['scheduler-calendar-settings'] });
      toast.success('Calendar settings saved');
    } catch (err) {
      toast.error('Failed to save calendar settings', getErrorMessage(err, ''));
    } finally {
      setSaving(false);
    }
  }

  const header = (
    <PageHeader
      title="Calendar Settings"
      subtitle="Scheduler appearance, status colors, appointment stage rules and the appointment info pop-up."
      actions={
        // Locked: nothing can change, so there is no Save.
        locked ? undefined : (
          <div className="flex flex-wrap items-center gap-3">
            {dirty && (
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1.5 text-sm text-blue-700">
                  <AlertTriangle className="h-4 w-4" aria-hidden />
                  Unsaved changes
                </span>
                <Button
                  variant="ghost"
                  size="xs"
                  className="h-7 px-2 text-xs text-slate-500 hover:text-slate-700"
                  leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
                  aria-label="Revert all changes"
                  onClick={() => setForm(savedForm)}
                  disabled={saving}
                >
                  Revert
                </Button>
              </div>
            )}
            <Button onClick={handleSave} disabled={!dirty || saving} loading={saving}>
              Save Settings
            </Button>
          </div>
        )
      }
    />
  );

  if (loading) {
    return (
      <div className="flex flex-col h-full overflow-hidden p-6">
        <div className="flex-shrink-0">{header}</div>
        <SpinnerOverlay label="Loading calendar settings…" />
      </div>
    );
  }

  // A failed load leaves form/settings null. Show the error and a retry rather
  // than an indefinite spinner.
  if (!form || !settings) {
    return (
      <div className="flex flex-col h-full overflow-hidden p-6">
        <div className="flex-shrink-0">{header}</div>
        <Alert variant="error" title="Could not load calendar settings">
          <p className="mb-3">{loadError ?? 'Please try again.'}</p>
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        </Alert>
      </div>
    );
  }

  const fixedRuleTitle = (stageId: string) =>
    settings.options.fixedRuleStageTitles[stageId] || `stage ${stageId}`;

  return (
    <div className="flex flex-col h-full overflow-hidden p-6">
      <div className="flex-shrink-0">{header}</div>

      <div className="flex-1 overflow-y-auto space-y-4 pb-4">
        <AdminLockedFieldset>
          {/* ─── Scheduler Display ─────────────────────────────────────────── */}
          <Card>
            <CardHeader>
              <CardTitle>Scheduler Display</CardTitle>
            </CardHeader>
            <CardBody className="grid gap-4 sm:grid-cols-2">
              {APPEARANCE_TOGGLES.map((t) => (
                <SwitchField
                  key={t.key}
                  id={`calendar-${t.key}`}
                  label={t.label}
                  checked={form.appearance[t.key] === 1}
                  onChange={(on) =>
                    patch((f) => ({
                      ...f,
                      appearance: { ...f.appearance, [t.key]: on ? 1 : 0 },
                    }))
                  }
                  badge={fieldPill('appearance', t.key, t.label)}
                />
              ))}
              <DialogField label="Font Size" htmlFor="calendar-font-size" badge={fieldPill('appearance', 'schedulerFontSize', 'Font Size')}>
                <Select<string>
                  id="calendar-font-size"
                  // As wide as its values, not the grid column.
                  wrapperClassName="w-fit"
                  options={FONT_SIZE_OPTIONS}
                  value={form.appearance.schedulerFontSize}
                  placeholder="Default"
                  onChange={(v) =>
                    patch((f) => ({
                      ...f,
                      appearance: { ...f.appearance, schedulerFontSize: v },
                    }))
                  }
                />
              </DialogField>
            </CardBody>
          </Card>

          {/* ─── Status Colors ─────────────────────────────────────────────── */}
          <Card>
            <CardHeader>
              <CardTitle>Status Colors</CardTitle>
            </CardHeader>
            <CardBody className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {PALETTE_FIELDS.map((f) => (
                <ColorField
                  key={f.key}
                  label={f.label}
                  badge={fieldPill('palette', f.key, f.label)}
                  value={form.palette[f.key]}
                  onChange={(next) =>
                    patch((draft) => ({
                      ...draft,
                      palette: { ...draft.palette, [f.key]: next },
                    }))
                  }
                />
              ))}
            </CardBody>
          </Card>

          {/* ─── Fixed appointment → stage rules ───────────────────────────── */}
          <Card>
            <CardHeader>
              <CardTitle>Appointment Stage Rules</CardTitle>
            </CardHeader>
            <CardBody className="space-y-4">
              {FIXED_RULES.map((rule) => {
                const options = [
                  { value: '0', label: 'do nothing' },
                  ...Object.entries(rule.stageTitles).map(([value, stageId]) => ({
                    value,
                    label: `change plan stage to ${fixedRuleTitle(stageId)}`,
                  })),
                ];

                return (
                  <div key={rule.key} className="grid gap-2 sm:grid-cols-2 sm:items-center">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-slate-700">{rule.label}</span>
                      {fieldPill('apptChangeRules', rule.key, rule.label)}
                    </div>
                    <Select<string>
                      options={options}
                      value={form.apptChangeRules[rule.key] ?? '0'}
                      onChange={(v) =>
                        patch((f) => ({
                          ...f,
                          apptChangeRules: { ...f.apptChangeRules, [rule.key]: v ?? '0' },
                        }))
                      }
                    />
                  </div>
                );
              })}
            </CardBody>
          </Card>

          {/* ─── Additional Rules repeater ─────────────────────────────────── */}
          <Card>
            <CardHeader
              action={
                locked ? undefined : (
                  <Button
                    variant="ghost"
                    // A rule must land on a stage, so there is nothing to add until
                    // the account has at least one active plan stage.
                    disabled={stageOptions.length === 0}
                    onClick={() =>
                      patch((f) => ({
                        ...f,
                        stageRules: [
                          ...f.stageRules,
                          {
                            rowKey: nextRuleKey(),
                            appointmentStageRuleID: 0,
                            type: typeOptions[0]?.value ?? 'consult',
                            apptAction: 'scheduled',
                            stageID: stageOptions[0].value,
                          },
                        ],
                      }))
                    }
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Rule
                  </Button>
                )
              }
            >
              <div className="flex items-center gap-2">
                <CardTitle>Additional Rules</CardTitle>
                {rulesPill}
              </div>
            </CardHeader>
            <CardBody className="space-y-3">
              {form.stageRules.length === 0 ? (
                <p className="text-sm text-slate-500">
                  No additional rules. These run after the fixed rules above and take precedence
                  over them.
                </p>
              ) : (
                form.stageRules.map((rule, index) => (
                  <div
                    key={rule.rowKey}
                    className="flex flex-wrap items-center gap-2 rounded border border-slate-200 p-3"
                  >
                    <span className="text-sm text-slate-700">When a</span>
                    <Select<string>
                      wrapperClassName="w-auto min-w-[11rem] flex-1"
                      options={typeOptions}
                      value={rule.type}
                      onChange={(v) => updateRule(index, { type: v ?? '' })}
                    />
                    <span className="text-sm text-slate-700">appointment is</span>
                    <Select<string>
                      wrapperClassName="w-auto min-w-[9rem]"
                      options={RULE_ACTION_OPTIONS}
                      value={rule.apptAction}
                      onChange={(v) => updateRule(index, { apptAction: v ?? '' })}
                    />
                    <span className="text-sm text-slate-700">change plan stage to</span>
                    <Select<number>
                      wrapperClassName="w-auto min-w-[11rem] flex-1"
                      options={stageOptions}
                      value={rule.stageID}
                      onChange={(v) => updateRule(index, { stageID: v ?? 0 })}
                    />
                    {!locked && (
                      <Button
                        variant="ghost"
                        aria-label="Remove rule"
                        onClick={() =>
                          patch((f) => ({
                            ...f,
                            stageRules: f.stageRules.filter((_, i) => i !== index),
                          }))
                        }
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    )}
                  </div>
                ))
              )}
            </CardBody>
          </Card>

          {/* ─── Info Pop-up ───────────────────────────────────────────────── */}
          <Card>
            <CardHeader>
              <CardTitle>Info Pop-up</CardTitle>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                {INFO_FLAGS.map((f) => {
                  const label = f.roleType ? `Plan ${roleLabel(f.roleType, f.label)}` : f.label;
                  return (
                    <div key={f.key} className="flex items-center gap-2">
                      <Checkbox checked={form.infoFlags[f.key] === 1} onChange={() => toggleInfoFlag(f.key)} label={label} />
                      {fieldPill('infoFlags', f.key, label)}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-start gap-2 border-t border-slate-200 pt-4">
                <Checkbox
                  checked={form.infoFlags.displayDailyAlert === 1}
                  onChange={() => toggleInfoFlag('displayDailyAlert')}
                  label="Prompt for pending appointment updates from the previous day"
                  description="Runs the previous-day reconciliation check when the scheduler opens."
                />
                {fieldPill('infoFlags', 'displayDailyAlert', 'Prompt for pending appointment updates')}
              </div>
            </CardBody>
          </Card>
        </AdminLockedFieldset>
      </div>
    </div>
  );

  function updateRule(index: number, changes: Partial<RuleRow>) {
    patch((f) => ({
      ...f,
      stageRules: f.stageRules.map((r, i) => (i === index ? { ...r, ...changes } : r)),
    }));
  }

  function toggleInfoFlag(key: string) {
    patch((f) => ({
      ...f,
      infoFlags: { ...f.infoFlags, [key]: f.infoFlags[key] === 1 ? 0 : 1 },
    }));
  }
}

// ─── Color field ──────────────────────────────────────────────────────────────

/**
 * Swatch + hex text, mirroring the legacy MiniColors picker's pairing. Native
 * `<input type="color">` is the established pattern in this codebase and needs
 * a full `#rrggbb` value, which the API already normalizes to.
 */
function ColorField({
  label,
  badge,
  value,
  onChange,
}: {
  label: string;
  /** Beside the label: the "modified" pill. */
  badge?: ReactNode;
  value: string | null;
  onChange: (value: string | null) => void;
}) {
  // Locked: the swatch only shows the colour (no picker); the hex is read-only.
  const locked = useFieldLocked();
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2">
        <label className="block text-sm font-medium text-slate-700">{label}</label>
        {badge}
      </div>
      <div className="flex items-center gap-2">
        {locked ? (
          <span aria-hidden="true" className="h-9 w-10 flex-shrink-0 rounded border border-slate-300 bg-white p-0.5">
            <span className="block h-full w-full rounded-sm" style={{ backgroundColor: value ?? '#ffffff' }} />
          </span>
        ) : (
          <input
            type="color"
            aria-label={`${label} color`}
            value={value ?? '#ffffff'}
            onChange={(e) => onChange(e.target.value)}
            className="h-9 w-10 flex-shrink-0 cursor-pointer rounded border border-slate-300 bg-white p-0.5"
          />
        )}
        <Input
          value={value ?? ''}
          placeholder="#000000"
          onChange={(e) => onChange(e.target.value.trim() === '' ? null : e.target.value)}
        />
      </div>
    </div>
  );
}
